import React, { useState } from 'react';
import { 
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { ApiResponse } from '../../services/apiPlaygroundService';

interface ResponseViewerProps {
  response: ApiResponse | null;
  isLoading: boolean;
}

const ResponseViewer: React.FC<ResponseViewerProps> = ({ response, isLoading }) => {
  const [activeTab, setActiveTab] = useState<'body' | 'headers'>('body');
  const [showRawJson, setShowRawJson] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-600 bg-green-50 border-green-200';
    if (status >= 400 && status < 500) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (status >= 500) return 'text-red-600 bg-red-50 border-red-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status: number) => {
    if (status >= 200 && status < 300) return CheckCircleIcon;
    if (status >= 400 && status < 500) return ExclamationCircleIcon;
    if (status >= 500) return XCircleIcon;
    return ClockIcon;
  };

  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatJson = (obj: any) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const renderResponseData = (data: any) => {
    if (!data) return null;

    // If it's a CrawlyCarl API response with structured data
    if (typeof data === 'object' && data.status) {
      return (
        <div className="space-y-4">
          {/* Status and Meta Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <span className={`ml-2 px-2 py-1 rounded text-xs ${
                  data.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {data.status}
                </span>
              </div>
              {data.meta && (
                <>
                  {data.meta.total_cost && (
                    <div>
                      <span className="font-medium text-gray-700">Cost:</span>
                      <span className="ml-2 text-gray-900">{data.meta.total_cost} credits</span>
                    </div>
                  )}
                  {data.meta.processing_time_ms && (
                    <div>
                      <span className="font-medium text-gray-700">Processing Time:</span>
                      <span className="ml-2 text-gray-900">{formatResponseTime(data.meta.processing_time_ms)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Main Data */}
          {data.data && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Response Data</h4>
              {data.data.complete_response && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complete Response
                  </label>
                  <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                    {data.data.complete_response}
                  </div>
                </div>
              )}
              
              {data.data.visited_urls && data.data.visited_urls.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visited URLs ({data.data.visited_urls.length})
                  </label>
                  <div className="bg-white border border-gray-200 rounded p-3">
                    {data.data.visited_urls.map((url: string, index: number) => (
                      <div key={index} className="text-sm text-blue-600 hover:text-blue-800">
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {url}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Info */}
          {data.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-900 mb-2">Error Details</h4>
              <div className="text-sm text-red-700">
                <div><strong>Code:</strong> {data.error.code}</div>
                <div><strong>Message:</strong> {data.error.message}</div>
                {data.error.details && (
                  <div className="mt-2">
                    <strong>Details:</strong>
                    <pre className="mt-1 text-xs bg-red-100 p-2 rounded overflow-x-auto">
                      {formatJson(data.error.details)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Raw JSON Toggle */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowRawJson(!showRawJson)}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              {showRawJson ? <EyeSlashIcon className="h-4 w-4 mr-1" /> : <EyeIcon className="h-4 w-4 mr-1" />}
              {showRawJson ? 'Hide' : 'Show'} Raw JSON
            </button>
          </div>

          {showRawJson && (
            <div className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm">{formatJson(data)}</pre>
            </div>
          )}
        </div>
      );
    }

    // Fallback for other response types
    return (
      <div className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
        <pre className="text-sm">{formatJson(data)}</pre>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Response</h3>
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Executing request...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!response) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Response</h3>
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <div className="text-center">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">No response yet</p>
            <p className="text-xs text-gray-500">Execute a request to see the response</p>
          </div>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(response.status);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Response</h3>
        <button
          onClick={() => copyToClipboard(formatJson(response.data))}
          className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
          Copy
        </button>
      </div>

      {/* Status Bar */}
      <div className={`flex items-center justify-between p-3 rounded-lg border ${getStatusColor(response.status)}`}>
        <div className="flex items-center space-x-2">
          <StatusIcon className="h-5 w-5" />
          <span className="font-medium">
            {response.status} {response.statusText}
          </span>
        </div>
        <div className="text-sm">
          {formatResponseTime(response.responseTime)}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {[
            { id: 'body' as const, name: 'Response Body' },
            { id: 'headers' as const, name: 'Headers' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'body' && (
          <div className="space-y-4">
            {renderResponseData(response.data)}
          </div>
        )}

        {activeTab === 'headers' && (
          <div className="space-y-2">
            {Object.entries(response.headers).map(([key, value]) => (
              <div key={key} className="flex items-start space-x-2 py-2 border-b border-gray-100">
                <span className="font-medium text-gray-700 min-w-0 flex-shrink-0 w-1/3">
                  {key}:
                </span>
                <span className="text-gray-900 break-all">
                  {value}
                </span>
              </div>
            ))}
            {Object.keys(response.headers).length === 0 && (
              <div className="text-center py-6 text-gray-500 text-sm">
                No headers in response
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponseViewer;
